import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api';
import images from '../../images';

const AdvertiserCard = ({ advertiser, onRefresh, onEditClick }) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [flows, setFlows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packagesMap, setPackagesMap] = useState({});
  const [leadsData, setLeadsData] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]); // Все уведомления
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);
  const notificationsRef = useRef([]); // Ссылка на текущие уведомления
  const allNotificationsRef = useRef([]); // Ссылка на все уведомления

  // Обновляем ref при изменении notifications
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Обновляем ref при изменении allNotifications
  useEffect(() => {
    allNotificationsRef.current = allNotifications;
  }, [allNotifications]);

  const formatMoney = (amount) => {
    if (!amount) return '0';
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const loadNotifications = async () => {
    try {
      const allNotificationResponse = await apiRequest(`/notification/${advertiser.id}/list?approve=false`, 'GET');
      const notificationsResponse = await apiRequest(`/approve/${advertiser.id}/list?status=wait`, 'GET');
      console.log("notificationsResponse: ", notificationsResponse);
      const allNewNotifications = allNotificationResponse.data.notifications || [];
      const newNotifications = notificationsResponse.data.items || [];
      
      // Проверяем, изменились ли данные для allNewNotifications
      const areAllNotificationsEqual = allNotificationsRef.current.length === allNewNotifications.length &&
        allNotificationsRef.current.every((notif, index) => notif.id === allNewNotifications[index].id &&
          notif.created_at === allNewNotifications[index].created_at);
      
      // Проверяем, изменились ли данные для newNotifications
      const areNotificationsEqual = notificationsRef.current.length === newNotifications.length &&
        notificationsRef.current.every((notif, index) => notif.id === newNotifications[index].id &&
          notif.created_at === newNotifications[index].created_at);
      
      // Обновляем только если данные изменились
      if (!areAllNotificationsEqual) {
        setAllNotifications(allNewNotifications);
      }
      if (!areNotificationsEqual) {
        setNotifications(newNotifications);
      }
    } catch (error) {
      console.error('Ошибка при загрузке уведомлений:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Загружаем потоки
        const flowsData = await apiRequest('flow-guides', 'GET');
        setFlows(flowsData.data || []);

        // Загружаем пакеты
        const packagesData = await apiRequest('paks', 'GET');
        setPackages(packagesData.data || []);

        // Делаем один запрос для всех лидов
        const packetsResponse = await apiRequest(`/pakets?rekl_id=${advertiser.id}`, 'GET');
        const packagesList = packetsResponse.data.pakets || [];

        const leadsResponse = await apiRequest(`leads/${advertiser.id}/list`, 'GET');
        const leadsList = leadsResponse.data.leads || [];
        // console.log("leadsResponse: ", leadsResponse);
        // console.log("packetsResponse: ", packetsResponse);
        const packagesMap = {};
        packagesList.forEach(pkg => {
          packagesMap[pkg.id] = pkg;
        });
        setPackagesMap(packagesMap);
        const newleadsList = leadsList.map(lead => {
          if (packagesMap[lead.paket]){
            lead.package = packagesMap[lead.paket];
          }
          return lead;
        });

        // Инициализируем leadsMap пустыми списками для каждого потока и пакета
        const leadsMap = {};
        for (const flow of flowsData.data || []) {
          const flowId = flow.id;
          if (!leadsMap[flowId]) {
            leadsMap[flowId] = {};
          }
          
          for (const pkg of packagesData.data || []) {
            leadsMap[flowId][pkg.id] = [];
          }
        }

        for (const lead of newleadsList) {
          // Добавляем защиту от undefined: проверяем, что lead.package существует и имеет нужные поля
          if (lead.package && lead.package.flow_guide_id && lead.package.paks_id) {
            if (leadsMap[lead.package.flow_guide_id] && leadsMap[lead.package.flow_guide_id][lead.package.paks_id]) {
              leadsMap[lead.package.flow_guide_id][lead.package.paks_id].push(lead);
            }
          }
        }
        // console.log("leadsMap: ", leadsMap);
        setLeadsData(leadsMap);

        // Загружаем уведомления для пользователя
        await loadNotifications();
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [advertiser.id]);

  // Перезапрос уведомлений каждые 5 секунд
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      loadNotifications();
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [advertiser.id]);

  const handlePasswordClick = async () => {
    setShowPassword(!showPassword);
  };

  const handleCardClick = () => {
    navigate(`/advertisers/profile/${advertiser.id}`);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEditClick) {
      onEditClick(advertiser);
    }
  };

  if (loading) {
    return (
      <div className="advertiser-card">
        <p>Загрузка...</p>
      </div>
    );
  }

  // Проверяем, есть ли непрочитанные уведомления
  const hasUnreadNotifications = ((notifications && notifications.length > 0) || (allNotifications && allNotifications.length > 0));

  return (
    <div className="advertiser-card" onClick={handleCardClick}>
      {/* Блок 1: Заголовок с id и tg_url */}
      <div className="advertiser-card-header">
        <div className="advertiser-card-header-row">
          <div className="advertiser-id-block">
            <span className="advertiser-id-value">ID: {advertiser.id}</span>
          </div>
          <div className="advertiser-tg-url-block">
            <span className="advertiser-tg-url-value">{advertiser.upk}</span>
          </div>
        </div>

        {hasUnreadNotifications && (
            <span className="notification-dot"></span>
        )}
      </div>

      {/* Блок 2: Название и email */}
      <div className="advertiser-card-content">
        <h3 className="advertiser-name">{advertiser.name}</h3>
        <div className="advertiser-email-row">
          <img src={images.letter} alt="email" className="email-icon" />
          <span className="advertiser-email">{advertiser.email}</span>
        </div>
        
        {/* Блок пароля */}
        {/*<div className="advertiser-password-row">*/}
        {/*  <div className="advertiser-password-block">*/}
        {/*    <img src={images.lockPasswordUnlockedOutline} alt="password" className="advertiser-password-icon" />*/}
        {/*    <span className="advertiser-password">*/}
        {/*      {showPassword ? '1111111111' : '**********'}*/}
        {/*    </span>*/}
        {/*  </div>*/}
        {/*  <button */}
        {/*    className="password-toggle-btn"*/}
        {/*    onClick={(e) => {*/}
        {/*      e.stopPropagation();*/}
        {/*      handlePasswordClick();*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    <img src={images.eyeLinear} alt="show" className="eye-icon" />*/}
        {/*  </button>*/}
        {/*</div>*/}

        {/* Блок 3: Потоки */}
        <div className="advertiser-flows-section">
          {flows.map(flow => {
            const flowLeads = leadsData[flow.id] || {};
            return (
              <div key={flow.id} className="flow-row">
                <span className="flow-name">{flow.flow_name}</span>
                <div className="flow-packages">
                  {packages.map((pkg, index) => {
                    const leads = flowLeads[pkg.id] || [];
                    // const separator = index < packages.length - 1 ? ' / ' : ' ';
                    const hasLeads = leads.length > 0;
                    return (
                      <span key={pkg.id} className="package-leads" data-count={leads.length}>
                        {hasLeads ? `${leads.length}` : '0'}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Блок 4: Кнопки */}
        <div className="advertiser-actions-section">
          <button 
            className="btn advertiser-money-btn"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={images.walletLinear} alt="money" className="wallet-icon" />
            <span className="money-amount">{formatMoney(advertiser.money)} ₽</span>
          </button>
          <button 
            className="btn advertiser-edit-btn" 
            onClick={handleEditClick}
          >
            <img src={images.penLinear} alt="edit" className="advertiser-pen-icon" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvertiserCard;
